'use client';
import React, { useEffect, useMemo, useState } from 'react';
import { alpha, Backdrop, Box, Fade, Modal, Typography, useTheme } from '@mui/material';

import { useAuth, useClobOrder, useSignInHandlers, useT } from '@/hooks';
import { useOrderBook, usePortfolioSummary } from '@/services/query';
import { useMarketPrices } from '@/services/query/markets.queries';
import { openModal, useAppDispatch } from '@/store';
import { setSetupRequested } from '@/store/slices/auth.slice';
import type { EventMarket, EventOutcome, EventResponse, Market, Outcome } from '@/types';
import {
  getPreferredOutcomeDisplayPrice,
  getSpreadLine,
  isMarketResolved,
  normalizeMarketQuestion,
} from '@/utils';
import {
  MobileAmountInput,
  MobileExpirationSelector,
  MobileLimitPriceInput,
  MobileMarketInfo,
  MobileOutcomeButton,
  MobileSharesInput,
  MobileTradeButton,
  MobileTradeHeader,
  MobileTradeSummary,
} from './mobiletrade';
import { ResolvedTradeView } from './ResolvedTradeView';

import { calculateExpirationTimestamp } from '@/constants/market.constants';

interface MobileTradeModalProps {
  event?: EventResponse;
  market: Market | EventMarket;
  outcome: Outcome | EventOutcome;
  isBuyOrder: boolean;
  isOpen: boolean;
  onClose: () => void;
  isResolved?: boolean;
  winnerName?: string;
  teamColor?: string;
}

export function MobileTradeModal({
  event,
  market,
  outcome,
  isBuyOrder,
  isOpen,
  onClose,
  isResolved,
  winnerName,
  teamColor,
}: MobileTradeModalProps) {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { isAuthenticated, user, isReadyToTrade } = useAuth();
  const { onLogin } = useSignInHandlers((error) => {
    if (error) console.error('Sign in error:', error);
  });

  const { data: portfolioSummary } = usePortfolioSummary();
  const { submitOrder, isSubmitting, error: orderError } = useClobOrder();
  const { t } = useT();

  // Initialize limit price from outcome price
  const initialLimitPrice = Math.round((Number(outcome?.price) || 0.43) * 100);

  // Mobile state
  const [orderType, setOrderType] = useState<'market' | 'limit'>('market');
  const [limitPrice, setLimitPrice] = useState(initialLimitPrice); // in cents
  const [shares, setShares] = useState('');
  const [amount, setAmount] = useState('0');
  const [setExpiration, setSetExpiration] = useState(false);
  const [activeTab, setActiveTab] = useState<'buy' | 'sell'>(isBuyOrder ? 'buy' : 'sell');
  const [expirationValue, setExpirationValue] = useState('1m');
  const [internalOutcome, setInternalOutcome] = useState(outcome);

  const tokenIds = useMemo(() => {
    try {
      if (typeof market.clobTokenIds === 'string') {
        return JSON.parse(market.clobTokenIds);
      }
      return market.clobTokenIds || [];
    } catch (e) {
      return [];
    }
  }, [market.clobTokenIds]);

  const { data: realtimePrices } = useMarketPrices(tokenIds, isOpen);

  const outcomesArray = useMemo(() => {
    try {
      return typeof market.outcomes === 'string'
        ? JSON.parse(market.outcomes)
        : Array.isArray(market.outcomes)
          ? market.outcomes
          : [];
    } catch {
      return [];
    }
  }, [market.outcomes]);

  const effectivePrices = realtimePrices;
  const selectedOutcomeIndex = useMemo(
    () => outcomesArray.findIndex((o: any) => o.id === internalOutcome?.id),
    [outcomesArray, internalOutcome?.id]
  );
  const selectedTokenId = useMemo(
    () =>
      selectedOutcomeIndex >= 0 && selectedOutcomeIndex < tokenIds.length
        ? tokenIds[selectedOutcomeIndex]
        : undefined,
    [selectedOutcomeIndex, tokenIds]
  );
  const { data: orderBook } = useOrderBook(
    selectedTokenId || '',
    Boolean(selectedTokenId && isOpen)
  );

  const isClosedMarket = useMemo(() => {
    return (
      (market as any)?.closed === true ||
      (market as any)?.closed === 'true' ||
      (market as any)?.status === 'closed' ||
      (market as any)?.status === 'resolved'
    );
  }, [market]);

  const currentPrice = useMemo(() => {
    const isSellTab = activeTab === 'sell';
    const staticPrice = Number(internalOutcome?.price ?? 0);
    const fallbackPrice = isSellTab
      ? ((internalOutcome as any)?.bestBid ?? staticPrice)
      : ((internalOutcome as any)?.bestAsk ?? staticPrice);

    const basePrice = getPreferredOutcomeDisplayPrice(
      isSellTab ? (internalOutcome as any)?.bestBid : (internalOutcome as any)?.bestAsk,
      fallbackPrice,
      { isClosed: isClosedMarket, bestBid: (internalOutcome as any)?.bestBid }
    );

    if (!selectedTokenId || !effectivePrices?.[selectedTokenId]) {
      return basePrice ?? staticPrice;
    }

    return isSellTab
      ? effectivePrices[selectedTokenId].bidPrice
      : effectivePrices[selectedTokenId].askPrice;
  }, [activeTab, effectivePrices, isClosedMarket, internalOutcome, selectedTokenId]);

  // Update limit price when activeTab or currentPrice changes
  useEffect(() => {
    if (isOpen && internalOutcome) {
      queueMicrotask(() => {
        setLimitPrice(Math.round((currentPrice || 0.43) * 100));
      });
    }
  }, [isOpen, activeTab, currentPrice, internalOutcome]);

  // Sync internalOutcome and activeTab when props change
  useEffect(() => {
    if (isOpen) {
      queueMicrotask(() => {
        setInternalOutcome(outcome);
        setActiveTab(isBuyOrder ? 'buy' : 'sell');
      });
    }
  }, [isOpen, outcome, isBuyOrder]);

  // Get market question
  const marketQuestion = market?.question || market?.title || 'Untitled Market';

  // Calculate values
  const sharesNum = parseFloat(shares.replace(/[^0-9.]/g, '')) || 0;
  const amountNum = parseFloat((amount || '0').replace(/[^0-9.]/g, '')) || 0;

  const effectivePrice = orderType === 'limit' ? limitPrice / 100 : currentPrice;

  const averageFillPrice = useMemo(() => {
    if (!orderBook || orderType !== 'market') return undefined;

    if (activeTab === 'buy' && amountNum > 0) {
      const asks = [...(orderBook.asks || [])].sort((a, b) => Number(a.price) - Number(b.price));
      let remainingAmount = amountNum;
      let totalShares = 0;
      let totalSpent = 0;

      for (const level of asks) {
        const levelPrice = Number(level.price);
        const levelSize = Number(level.size);
        if (!levelPrice || !levelSize || levelPrice <= 0 || levelSize <= 0) continue;

        const levelCost = levelPrice * levelSize;
        if (remainingAmount >= levelCost) {
          totalSpent += levelCost;
          totalShares += levelSize;
          remainingAmount -= levelCost;
        } else {
          const partialShares = remainingAmount / levelPrice;
          totalSpent += remainingAmount;
          totalShares += partialShares;
          break;
        }
      }

      return totalShares > 0 ? totalSpent / totalShares : undefined;
    }

    if (activeTab === 'sell' && sharesNum > 0) {
      const bids = [...(orderBook.bids || [])].sort((a, b) => Number(b.price) - Number(a.price));
      let remainingShares = sharesNum;
      let soldShares = 0;
      let received = 0;

      for (const level of bids) {
        const levelPrice = Number(level.price);
        const levelSize = Number(level.size);
        if (!levelPrice || !levelSize || levelPrice <= 0 || levelSize <= 0) continue;

        const fillShares = Math.min(remainingShares, levelSize);
        soldShares += fillShares;
        received += fillShares * levelPrice;
        remainingShares -= fillShares;
        if (remainingShares <= 0) break;
      }

      return soldShares > 0 ? received / soldShares : undefined;
    }

    return undefined;
  }, [orderBook, orderType, activeTab, amountNum, sharesNum]);

  // Calculate shares and cost based on mode
  let calculatedShares = 0;
  let totalCost = 0;

  if (activeTab === 'buy') {
    if (orderType === 'market') {
      totalCost = amountNum;
      const execPrice = averageFillPrice || effectivePrice;
      calculatedShares = execPrice > 0 ? amountNum / execPrice : 0;
    } else {
      totalCost = sharesNum * effectivePrice;
      calculatedShares = sharesNum;
    }
  } else {
    // Sell
    totalCost = sharesNum * effectivePrice; // These are proceeds
    calculatedShares = sharesNum;
  }

  const potReturn = activeTab === 'buy' ? calculatedShares : totalCost;
  const potProfit = activeTab === 'buy' ? Math.max(0, calculatedShares - totalCost) : 0;

  // Handlers
  function handleLimitPriceDecrease() {
    setLimitPrice((prev) => Math.max(1, prev - 1));
  }

  function handleLimitPriceIncrease() {
    setLimitPrice((prev) => Math.min(99, prev + 1));
  }

  function handleSharesChange(e: React.ChangeEvent<HTMLInputElement>) {
    const value = e.target.value.replace(/[^0-9.]/g, '');
    const numValue = parseFloat(value);
    if (numValue > 10000000) {
      setShares('10000000');
    } else {
      setShares(value);
    }
  }

  function handleShareQuickAmount(amount: number) {
    const current = parseFloat(shares.replace(/[^0-9.]/g, '')) || 0;
    const nextVal = Math.min(10000000, Math.max(0, current + amount));
    setShares(nextVal.toString());
  }

  function handleAmountDecrease() {
    const current = parseFloat((amount || '0').replace(/[^0-9.]/g, '')) || 0;
    setAmount(Math.max(0, current - 1).toString());
  }

  function handleAmountIncrease() {
    const current = parseFloat((amount || '0').replace(/[^0-9.]/g, '')) || 0;
    const nextVal = Math.min(10000000, current + 1);
    setAmount(nextVal.toString());
  }

  function handleAmountChange(e: React.ChangeEvent<HTMLInputElement>) {
    const value = e.target.value.replace(/[^0-9.]/g, '');
    // Allow empty string or one decimal point
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      const numValue = parseFloat(value);
      if (numValue > 10000000) {
        setAmount('10000000');
      } else {
        setAmount(value);
      }
    }
  }

  function handleAmountQuickAdd(addAmount: number | 'max') {
    if (addAmount === 'max') {
      const cappedBalance = Math.min(10000000, balance);
      setAmount(cappedBalance > 0 ? cappedBalance.toFixed(2) : '0');
    } else {
      const current = parseFloat((amount || '0').replace(/[^0-9.]/g, '')) || 0;
      const nextVal = Math.min(10000000, current + addAmount);
      setAmount(nextVal.toString());
    }
  }

  const balance = portfolioSummary?.cashBalance || 0;
  const isInsufficientBalance = activeTab === 'buy' && totalCost > balance && totalCost > 0;

  const buttonText = useMemo(() => {
    if (isSubmitting) return t('trade.processing');
    if (!isAuthenticated) return t('trade.trade');
    if (!isReadyToTrade) return t('trade.loading');
    if (isInsufficientBalance) return t('trade.deposit');

    // Check for no bids when selling market order
    if (
      activeTab === 'sell' &&
      orderType === 'market' &&
      (!orderBook?.bids || orderBook.bids.length === 0)
    ) {
      return t('trade.noBids');
    }

    // Check for no asks when buying market order
    if (
      activeTab === 'buy' &&
      orderType === 'market' &&
      (!orderBook?.asks || orderBook.asks.length === 0)
    ) {
      return t('trade.noAsks');
    }

    // Min size validations
    if (activeTab === 'buy') {
      if (orderType === 'market' && amountNum > 0 && amountNum < 1) return t('trade.minAmount');
      if (orderType === 'limit' && sharesNum > 0 && sharesNum < 5) return t('trade.minLimitShares');
    } else {
      if (orderType === 'market' && sharesNum > 0 && sharesNum < 1) return t('trade.minShares');
      if (orderType === 'limit' && sharesNum > 0 && sharesNum < 5) return t('trade.minLimitShares');
    }

    return activeTab === 'buy' ? t('trade.buy') : t('trade.sell');
  }, [
    isSubmitting,
    isAuthenticated,
    isReadyToTrade,
    isInsufficientBalance,
    activeTab,
    orderType,
    amountNum,
    sharesNum,
    orderBook,
    t,
  ]);

  // Update header text to show market question instead of generic event title
  const marketTitle = normalizeMarketQuestion(market);

  const isInputInvalid =
    isAuthenticated &&
    isReadyToTrade &&
    ((activeTab === 'buy' && orderType === 'market' && amountNum < 1) ||
      (orderType === 'limit' && (limitPrice <= 0 || sharesNum < 5)) ||
      (activeTab === 'sell' && orderType === 'market' && sharesNum < 1) ||
      (activeTab === 'sell' &&
        orderType === 'market' &&
        (!orderBook?.bids || orderBook.bids.length === 0)) ||
      (activeTab === 'buy' &&
        orderType === 'market' &&
        (!orderBook?.asks || orderBook.asks.length === 0)));

  async function handlePrimaryAction() {
    if (!isAuthenticated) {
      onLogin();
      return;
    }
    if (!user?.isReferredUser) {
      dispatch(openModal('referral'));
      return;
    }
    if (!isReadyToTrade) {
      dispatch(setSetupRequested(true));
      return;
    }
    if (isInsufficientBalance) {
      dispatch(openModal('deposit'));
      return;
    }
    handleTrade();
  }

  function handleToggleOutcome() {
    let outcomesArray: any[] = [];
    try {
      outcomesArray =
        typeof market.outcomes === 'string'
          ? JSON.parse(market.outcomes)
          : Array.isArray(market.outcomes)
            ? market.outcomes
            : [];
    } catch (e) {
      // ignore
    }

    if (outcomesArray.length >= 2) {
      const currentIndex = outcomesArray.findIndex((o) => o.id === internalOutcome.id);
      const nextIndex = (currentIndex + 1) % outcomesArray.length;
      setInternalOutcome(outcomesArray[nextIndex]);
    }
  }

  async function handleTrade() {
    if (!isAuthenticated) {
      onLogin();
      return;
    }
    if (!user?.isReferredUser) {
      dispatch(openModal('referral'));
      return;
    }
    dispatch(setSetupRequested(true));

    let size = sharesNum;

    if (activeTab === 'buy' && orderType === 'market') {
      const execPrice = averageFillPrice || currentPrice;
      size = execPrice > 0 ? amountNum / execPrice : 0;
    }

    // Basic validation
    if (size <= 0 && amountNum <= 0 && orderType === 'market') {
      console.error('Invalid order size or amount');
      return;
    }

    let expiration: number | undefined = undefined;
    if (orderType === 'limit' && setExpiration) {
      expiration = calculateExpirationTimestamp(expirationValue);
    }

    try {
      const side = activeTab.toUpperCase() as 'BUY' | 'SELL';
      await submitOrder({
        tokenId: selectedTokenId || internalOutcome.id,
        amount: side === 'BUY' && orderType === 'market' ? amountNum : size,
        price: orderType === 'limit' ? limitPrice / 100 : undefined,
        side,
        userUSDCBalance: balance,
        expiration,
      });
      onClose();
    } catch (err) {
      console.error('Error placing order:', err);
    }
  }
  const { outcomeLabel, outcomeColor, isNoOutcome, dynamicGameTitle } = useMemo(() => {
    const lowerName = (internalOutcome?.name || '').toLowerCase().trim();
    const homeName = event?.homeTeam?.name?.toLowerCase().trim() || '';
    const awayName = event?.awayTeam?.name?.toLowerCase().trim() || '';
    const homeAbbr = event?.homeTeam?.abbreviation?.toLowerCase().trim() || '';
    const awayAbbr = event?.awayTeam?.abbreviation?.toLowerCase().trim() || '';

    const marketTitle =
      (market as any)?.groupItemTitle || (market as any)?.question || (market as any)?.title || '';
    const lowerMarketTitle = marketTitle.toLowerCase();
    const searchableText = `${lowerName} ${lowerMarketTitle}`;

    const detectTeamFromText = (text: string) => {
      const normalized = text.toLowerCase().trim();
      if (
        (homeName && (normalized.includes(homeName) || homeName.includes(normalized))) ||
        (homeAbbr && (normalized.includes(homeAbbr) || homeAbbr.includes(normalized)))
      ) {
        return event?.homeTeam;
      }
      if (
        (awayName && (normalized.includes(awayName) || awayName.includes(normalized))) ||
        (awayAbbr && (normalized.includes(awayAbbr) || awayAbbr.includes(normalized)))
      ) {
        return event?.awayTeam;
      }
      return null;
    };

    let matchingTeam: any = null;

    const isDraw =
      searchableText.includes('draw') ||
      lowerMarketTitle.includes('draw') ||
      lowerName.includes('draw');
    searchableText.includes('dibujar') ||
      lowerMarketTitle.includes('dibujar') ||
      lowerName.includes('dibujar');

    if (isDraw) {
      matchingTeam = {
        name: t('sports.draw', { defaultValue: 'Draw' }),
        abbreviation: t('sports.draw', { defaultValue: 'Draw' }).toUpperCase(),
        color: theme.palette.mode === 'dark' ? '#94a3b8' : '#64748b',
      } as any;
    } else {
      matchingTeam = detectTeamFromText(searchableText);
    }

    const type = (
      (market as any)?.sportsMarketType ||
      (market as any)?.sportsType ||
      ''
    ).toLowerCase();
    const isTotal =
      type.includes('total') || type.includes('over/under') || lowerMarketTitle.includes('o/u');
    const line = (market as any)?.groupItemThreshold || (market as any)?.line || '';

    let finalOutcomeLabel = internalOutcome?.name || '';
    const lowerOutcome = (internalOutcome?.name || '').toLowerCase();

    if (lowerOutcome === 'yes' || lowerOutcome === 'no') {
      const selectionLabel =
        (market as any)?.groupItemTitle ||
        (market as any)?.question ||
        (market as any)?.title ||
        '';
      if (selectionLabel) {
        finalOutcomeLabel = selectionLabel;
      }
    }

    if (!isDraw) {
      const labelTeam = detectTeamFromText(finalOutcomeLabel);
      if (labelTeam) {
        matchingTeam = labelTeam;
      } else if (lowerOutcome === 'yes' || lowerOutcome === 'no') {
        const titleTeam = detectTeamFromText(lowerMarketTitle);
        if (titleTeam) {
          if (lowerOutcome === 'yes') {
            matchingTeam = titleTeam;
          } else {
            matchingTeam = titleTeam === event?.homeTeam ? event?.awayTeam : event?.homeTeam;
          }
        }
      }

      if (isTotal) {
        if (lowerOutcome === 'yes' || lowerOutcome === 'over') {
          finalOutcomeLabel = `OVER`.toUpperCase();
        } else if (lowerOutcome === 'no' || lowerOutcome === 'under') {
          finalOutcomeLabel = `UNDER`.toUpperCase();
        }
      }
    }

    const isSpread = type.includes('spread') || type.includes('handicap') || type === 'puck line';

    if (!type.includes('moneyline') && line && !finalOutcomeLabel.includes(String(line))) {
      if (isSpread) {
        const isAway =
          (awayName && (lowerOutcome.includes(awayName) || awayName.includes(lowerOutcome))) ||
          (awayAbbr && (lowerOutcome.includes(awayAbbr) || awayAbbr.includes(lowerOutcome)));
        const spreadLine = getSpreadLine(
          market as any,
          isAway ? event?.awayTeam?.name : event?.homeTeam?.name,
          isAway
        );
        if (spreadLine) {
          finalOutcomeLabel = `${finalOutcomeLabel} ${spreadLine}`;
        } else {
          finalOutcomeLabel = `${finalOutcomeLabel}`;
        }
      } else {
        finalOutcomeLabel = `${finalOutcomeLabel}`;
      }
    }

    let displayColor = teamColor || matchingTeam?.color || '#3b82f6';

    // Standard color logic for multi-outcome and sports markets
    if (isTotal || isDraw) {
      if (lowerOutcome === 'yes' || lowerOutcome.includes('over')) {
        displayColor = '#359a5e'; // Green
      } else if (lowerOutcome === 'no' || lowerOutcome.includes('under')) {
        displayColor = '#cb3131'; // Red
      } else if (isDraw) {
        displayColor = '#475569'; // Theme-aware slate for Draw markets
      }
    }

    const hTitle = event?.homeTeam?.name;
    const aTitle = event?.awayTeam?.name;

    let baseTitle = normalizeMarketQuestion(market);
    const genericTitlePattern = /^home\s*vs\.?\s*away$/i;
    if (event?.title && !genericTitlePattern.test(event.title)) {
      baseTitle = event.title;
    } else if (genericTitlePattern.test(baseTitle)) {
      baseTitle = (market as any).question || 'Market Details';
    }

    const gameFullTitle = hTitle && aTitle ? `${hTitle} vs. ${aTitle}` : baseTitle;

    const dynamicGameTitle = isTotal
      ? 'Over vs Under'
      : isDraw
        ? `Draw: ${gameFullTitle}`
        : gameFullTitle;

    let finalDisplayLabel = isDraw
      ? t('sports.draw', { defaultValue: 'Draw' }).toUpperCase()
      : finalOutcomeLabel;
    if (matchingTeam?.abbreviation) {
      const isSpreadMatch = isSpread && !isDraw;
      const isAway =
        (awayName && (lowerOutcome.includes(awayName) || awayName.includes(lowerOutcome))) ||
        (awayAbbr && (lowerOutcome.includes(awayAbbr) || awayAbbr.includes(lowerOutcome)));

      const spreadLine = isSpreadMatch
        ? getSpreadLine(
            market as any,
            isAway ? event?.awayTeam?.name : event?.homeTeam?.name,
            isAway
          )
        : null;

      finalDisplayLabel = matchingTeam.abbreviation;
      if (spreadLine) {
        finalDisplayLabel = `${finalDisplayLabel} ${spreadLine}`;
      } else if (lowerOutcome === 'yes') {
        finalDisplayLabel = `${finalDisplayLabel} - YES`;
      } else if (lowerOutcome === 'no') {
        finalDisplayLabel = `${finalDisplayLabel} - NO`;
      }
    }

    return {
      outcomeLabel: finalDisplayLabel.toUpperCase(),
      outcomeColor: displayColor,
      isNoOutcome: lowerOutcome === 'no' || lowerOutcome === 'under',
      dynamicGameTitle,
    };
  }, [event, internalOutcome, market, theme]);

  const content = (
    <Box
      sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 'calc(100% - 24px)',
        maxWidth: '450px',
        maxHeight: 'calc(100vh - 40px)',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
        borderRadius: '16px',
        bgcolor: 'background.paper',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        outline: 'none',
      }}
    >
      {isResolved ? (
        <Box sx={{ p: 2 }}>
          <ResolvedTradeView event={event} market={market} />
        </Box>
      ) : (
        <>
          <MobileTradeHeader
            activeTab={activeTab}
            onTabChange={setActiveTab}
            orderType={orderType}
            onOrderTypeChange={setOrderType}
          />

          <Box
            sx={{
              p: 2,
              overflowY: 'auto',
              flex: 1,
              minHeight: 0,
              '&::-webkit-scrollbar': { display: 'none' },
              msOverflowStyle: 'none',
              scrollbarWidth: 'none',
            }}
          >
            <MobileMarketInfo event={event} title={dynamicGameTitle} />

            <MobileOutcomeButton
              outcomeLabel={outcomeLabel}
              outcomeColor={outcomeColor}
              isNoOutcome={isNoOutcome}
              price={currentPrice}
              onClick={handleToggleOutcome}
            />

            {activeTab === 'buy' && orderType === 'market' && (
              <MobileAmountInput
                amount={amount}
                onAmountChange={handleAmountChange}
                onDecrease={handleAmountDecrease}
                onIncrease={handleAmountIncrease}
                onQuickAdd={handleAmountQuickAdd}
              />
            )}

            {(activeTab === 'sell' || orderType === 'limit') && (
              <>
                {orderType === 'limit' && (
                  <MobileLimitPriceInput
                    limitPrice={limitPrice}
                    onDecrease={handleLimitPriceDecrease}
                    onIncrease={handleLimitPriceIncrease}
                  />
                )}

                <MobileSharesInput
                  shares={shares}
                  onSharesChange={handleSharesChange}
                  onQuickAmount={handleShareQuickAmount}
                />

                {orderType === 'limit' && (
                  <MobileExpirationSelector
                    setExpiration={setExpiration}
                    onExpirationToggle={setSetExpiration}
                    expirationValue={expirationValue}
                    onExpirationValueChange={setExpirationValue}
                  />
                )}
              </>
            )}

            <MobileTradeSummary
              activeTab={activeTab}
              totalCost={totalCost}
              potReturn={potReturn}
              potProfit={potProfit}
              avgPrice={orderType === 'market' ? averageFillPrice || effectivePrice : undefined}
            />
          </Box>

          <MobileTradeButton
            onClick={handlePrimaryAction}
            disabled={isSubmitting || (isReadyToTrade && isInputInvalid)}
            buttonText={buttonText}
          />
        </>
      )}
    </Box>
  );

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
        sx: {
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          backdropFilter: 'blur(4px)',
        },
      }}
      sx={{
        zIndex: 10000,
      }}
    >
      <Fade in={isOpen}>{content}</Fade>
    </Modal>
  );
}

export default MobileTradeModal;
